import { AnyAction } from 'redux';

import { TabNames } from 'const/check';
import {
  IChangeTabName,
  ICheckCategoryChange,
  ICheckSelection,
  IExternalId,
  IFetchAssessmentPageError,
  IFetchAssessmentPageSuccess,
  IPageChange,
  IScaleChange,
  IToggleBordersVisibility,
} from 'actions/assessmentPage';
import {
  CHECK_CATEGORY_CHANGE,
  CHECK_SELECTION,
  FETCH_ASSESSMENT_PAGE_ERROR,
  FETCH_ASSESSMENT_PAGE_START,
  FETCH_ASSESSMENT_PAGE_SUCCESS,
  PAGE_CHANGE,
  SCALE_CHANGE,
  SET_EXTERNAL_ID,
  SET_PREV_EXTERNAL_ID,
  SET_QUALITY_CHECK_TABNAME,
  TOGGLE_BORDERS_VISIBILITY,
} from 'actions/assessmentPage/types';
import { ReviewResult } from 'types/check';
import { AggregatedAssessmentPage } from 'types/check-results';
import { Error } from 'types/error';
import { Review } from 'types/review-category';
import { preparePage } from 'utils/checks';

export type AssessmentPageReducerType = {
  isLoading: boolean;
  currentPage: AggregatedAssessmentPage | null;
  error: Error | null;
  selectedCheck: string | null;
  externalId: string | null;
  prevExternalId: string | null;
  selectedCategory: Review;
  selectedPage: number;
  selectedResult: ReviewResult | null;
  selectedTabName: TabNames;
  isBordersVisible: boolean;
  scale: number;
};

export const initialState = (): AssessmentPageReducerType => ({
  isLoading: false,
  selectedPage: 1,
  currentPage: null,
  error: null,
  selectedCheck: null,
  externalId: null,
  prevExternalId: null,
  selectedResult: null,
  selectedCategory: Review.safety,
  selectedTabName: TabNames.safety,
  isBordersVisible: true,
  scale: 1,
});

function handleFetchStart(state: AssessmentPageReducerType) {
  return {
    ...state,
    isLoading: true,
    currentPage: null,
    error: null,
  };
}

function handleFetchSuccess(
  state: AssessmentPageReducerType,
  action: IFetchAssessmentPageSuccess
) {
  return {
    ...state,
    isLoading: false,
    currentPage: preparePage(action.currentPage),
  };
}

function handleFetchError(
  state: AssessmentPageReducerType,
  action: IFetchAssessmentPageError
) {
  return {
    ...state,
    isLoading: false,
    error: { ...action.error },
  };
}

function handleCheckSelection(
  state: AssessmentPageReducerType,
  action: ICheckSelection
) {
  return {
    ...state,
    selectedCheck: action.id,
  };
}

function handleTabNameSelection(
  state: AssessmentPageReducerType,
  action: IChangeTabName
) {
  return {
    ...state,
    selectedTabName: action.tabTitle,
  };
}

function handleSetExternalId(
  state: AssessmentPageReducerType,
  action: IExternalId
) {
  return {
    ...state,
    externalId: action.id,
  };
}

function handleSetPrevExternalId(
  state: AssessmentPageReducerType,
  action: IExternalId
) {
  return {
    ...state,
    prevExternalId: action.id,
  };
}

function handleToggleBordersVisibility(
  state: AssessmentPageReducerType,
  _: IToggleBordersVisibility
) {
  return {
    ...state,
    isBordersVisible: !state.isBordersVisible,
  };
}

function handlePageChange(
  state: AssessmentPageReducerType,
  action: IPageChange
) {
  return {
    ...state,
    isLoading: true,
    currentPage: null,
    selectedPage: action.pageNumber,
    selectedCheck: null,
    externalId: null,
  };
}

function handleScaleChange(
  state: AssessmentPageReducerType,
  action: IScaleChange
) {
  return {
    ...state,
    scale: action.scale,
  };
}

function handleCheckCategoryChange(
  state: AssessmentPageReducerType,
  action: ICheckCategoryChange
) {
  return {
    ...state,
    selectedCheck: null,
    externalId: null,
    selectedCategory: action.category,
    selectedResult: action.result,
  };
}

export default function AssessmentPageReducer(
  state = initialState(),
  action: AnyAction
) {
  switch (action.type) {
    case FETCH_ASSESSMENT_PAGE_START:
      return handleFetchStart(state);
    case FETCH_ASSESSMENT_PAGE_SUCCESS:
      return handleFetchSuccess(state, action as IFetchAssessmentPageSuccess);
    case FETCH_ASSESSMENT_PAGE_ERROR:
      return handleFetchError(state, action as IFetchAssessmentPageError);
    case PAGE_CHANGE:
      return handlePageChange(state, action as IPageChange);
    case SCALE_CHANGE:
      return handleScaleChange(state, action as IScaleChange);
    case CHECK_CATEGORY_CHANGE:
      return handleCheckCategoryChange(state, action as ICheckCategoryChange);
    case CHECK_SELECTION:
      return handleCheckSelection(state, action as ICheckSelection);
    case SET_EXTERNAL_ID:
      return handleSetExternalId(state, action as IExternalId);
    case SET_PREV_EXTERNAL_ID:
      return handleSetPrevExternalId(state, action as IExternalId);
    case SET_QUALITY_CHECK_TABNAME:
      return handleTabNameSelection(state, action as IChangeTabName);
    case TOGGLE_BORDERS_VISIBILITY:
      return handleToggleBordersVisibility(
        state,
        action as IToggleBordersVisibility
      );
    default:
      return state;
  }
}
